/**
 * Detalle de inmueble.
 *
 * ORGANIZADO EN PESTAÑAS, y la razón no es que hubiera «demasiado contenido»: es que hasta
 * ahora esta pantalla enseñaba el RESULTADO de unos datos que no se podían tocar. El desglose
 * de gastos sumaba préstamos, seguros y gastos recurrentes; el patrimonio salía de la última
 * valoración. Ninguna de esas cosas tenía interfaz, aunque sus endpoints llevaban escritos y
 * probados desde la fase 2A.
 *
 * «Resumen» conserva la pantalla de la fase 1 —galería a la izquierda; valoración, mapa,
 * gastos y comentarios apilados a la derecha— y las demás se añaden al lado. La pestaña activa vive en la URL (`?tab=`), así que se puede enlazar,
 * recargar y volver atrás.
 *
 * «Editar» y «Borrar» viven en `properties/PropertyActions`: son lo único de la cabecera que
 * escribe, y sacarlos de aquí evita meter cuatro `useState` de diálogos en una pantalla que
 * por lo demás solo compone.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, queryKeys } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { Icon } from '../components/Icon';
import { ErrorState } from '../components/EmptyState';
import { SkeletonCard, SkeletonPageHeader } from '../components/Skeleton';
import { Tabs, type TabDefinition } from '../components/Tabs';
import { PropertyMap } from '../components/map/LazyPropertyMap';
import { MapModal } from '../components/map/MapModal';
import type { MapMarker } from '../components/map/PropertyMap';
import { PropertyActions } from './properties/PropertyActions';
import { PropertyPhotos } from './properties/PropertyPhotos';
import { PropertyUnits } from './properties/PropertyUnits';
import { PropertyFinancing } from './properties/PropertyFinancing';
import { PropertyInfoButton } from './properties/PropertyInfoCard';
import { PropertyDeeds } from './properties/PropertyDeeds';
import { PropertyFloorPlan } from './properties/PropertyFloorPlan';
import { useCan } from '../auth/AuthContext';
import { formatEur, formatNumber, formatPercent, formatEurCents, formatShortDate, formatSignedEur } from '../lib/format';
import { ProgressBar } from '../components/ProgressBar';
import { sumMoney } from '../lib/moneyInput';
import type { PropertyDetailResponse } from '@naar/shared';
import { InvestmentCosts } from './properties/InvestmentCosts';
import styles from './PropertyDetailPage.module.css';

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>();
  // Los permisos se leen arriba del todo: por debajo hay `return` tempranos, y un hook detrás
  // de uno de ellos deja de ejecutarse en cuanto la pantalla entra en carga o en error.
  const canPhotos = useCan('photo:manage');
  const canUnits = useCan('unit:manage');
  const canFinance = useCan('finance:manage');
  const canDocuments = useCan('document:manage');
  // El mapa pequeño se abre grande en un modal: aquí se guarda si está abierto.
  const [mapOpen, setMapOpen] = useState(false);
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.property(id ?? ''),
    queryFn: () => api.property(id!),
    enabled: Boolean(id),
  });

  if (isPending) {
    return (
      <section>
        <SkeletonPageHeader />
        <div className={styles.topGrid}>
          <SkeletonCard rows={1} height={280} />
          <div className={styles.sideColumn}>
            <SkeletonCard rows={2} height={110} />
            <SkeletonCard rows={3} height={90} />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <PageHeader title="Inmueble" backTo="/propiedades" backLabel="Volver a Propiedades" />
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  const investment = sumMoney([data.acquisitionPrice, data.acquisitionCosts]);
  // Lo que vale hoy menos lo que se ha metido (precio + costes), no solo el precio de compra.
  const marketGain = Number(data.valuation.current) - Number(investment);

  // El mismo pin para el mapa de la ficha y para el ampliado.
  const mapMarkers: MapMarker[] =
    data.latitude !== null && data.longitude !== null
      ? [
          {
            id: data.id,
            label: data.name,
            detail: `${data.addressLine} · ${data.city}`,
            latitude: data.latitude,
            longitude: data.longitude,
            // Un pin en el centro del pueblo NO puede parecer la puerta de casa.
            approximate:
              data.geocodePrecision === 'municipio' || data.geocodePrecision === 'calle',
          },
        ]
      : [];

  const financiacionRegistrada =
    data.financing.loans.length + data.financing.insurance.length + data.financing.recurringExpenses.length;
  const mostrarFinanciacion = data.financingEnabled || financiacionRegistrada > 0;

  const tabs: TabDefinition[] = [
    {
      id: 'resumen',
      label: 'Resumen',
      render: () => (
        <>
          {/* --- Galería + valoración ------------------------------------------ */}
          <div className={styles.topGrid}>
            <Card variant="sm" style={{ padding: 20 }}>
              {/* Los datos básicos van ARRIBA, antes de la galería: son lo primero que se busca
                  al abrir la ficha y, con varias fotos, quedaban por debajo del pliegue. */}
              <div className={styles.facts}>
                <div className={styles.fact}>
                  <div className={styles.factValue}>{data.bedrooms ?? '—'}</div>
                  <div className={styles.factLabel}>Habitaciones</div>
                </div>
                <div className={styles.fact}>
                  <div className={styles.factValue}>{data.bathrooms ?? '—'}</div>
                  <div className={styles.factLabel}>Baños</div>
                </div>
                <div className={styles.fact}>
                  <div className={styles.factValue}>{formatNumber(data.areaM2)}</div>
                  <div className={styles.factLabel}>m²</div>
                </div>
                <div className={styles.fact}>
                  <div className={styles.factValue}>{data.builtYear ?? '—'}</div>
                  <div className={styles.factLabel}>Año</div>
                </div>
              </div>

              <PropertyPhotos propertyId={data.id} photos={data.photos} canManage={canPhotos} />
            </Card>

            {/* Columna derecha: valoración y mapa, y debajo gastos y comentarios. Antes
                gastos y comentarios iban en una fila aparte de tres columnas y quedaban
                despegados de todo, con un tercio de la fila vacío. */}
            <div className={styles.sideColumn}>
              <Card variant="sm">
                {/*
                  Lo que se ha METIDO en el inmueble (precio de compra más los costes) y, al
                  lado, lo que VALE hoy: la última valoración, con su fecha y la diferencia con
                  la inversión. Sin ninguna valoración no se inventa una cifra: «Sin valorar».
                */}
                <div className={styles.valuationHead}>
                  <div>
                    <div className={styles.valuationLabel}>Inversión inmueble</div>
                    <div className={styles.valuationMeta}>(Precio + costes)</div>
                    <div className={styles.valuationValue}>{formatEurCents(investment)}</div>
                  </div>
                  <div className={styles.marketValue}>
                    <div className={styles.valuationLabel}>Valor de mercado</div>
                    <div className={styles.valuationMeta}>
                      {data.valuation.latest
                        ? `(Valorado el ${formatShortDate(data.valuation.latest.valuedAt)})`
                        : '(Sin valoración)'}
                    </div>
                    {data.valuation.latest ? (
                      <>
                        <div className={styles.valuationValue}>{formatEurCents(data.valuation.current)}</div>
                        <div
                          className={
                            marketGain >= 0 ? `${styles.marketGain} ${styles.marketGainUp}` : `${styles.marketGain} ${styles.marketGainDown}`
                          }
                        >
                          {formatSignedEur(marketGain)}
                          {Number(investment) > 0 &&
                            ` (${marketGain >= 0 ? '+' : '−'}${formatPercent(Math.abs((marketGain / Number(investment)) * 100))})`}{' '}
                          sobre la inversión
                        </div>
                      </>
                    ) : (
                      <div className={`${styles.valuationValue} ${styles.marketValueEmpty}`}>Sin valorar</div>
                    )}
                  </div>
                </div>

                <dl className={styles.investment}>
                  <div className={styles.investmentRow}>
                    <dt>Precio de compra</dt>
                    <dd>{formatEurCents(data.acquisitionPrice)}</dd>
                  </div>
                  {/* Cada coste con su motivo; los que no lo tienen se nombran aquí mismo. */}
                  <InvestmentCosts property={data} />
                </dl>

                <div className={styles.addressLabel}>Dirección</div>
                <div className={styles.addressText}>
                  {data.addressLine}
                  {data.postalCode ? `, ${data.postalCode}` : ''} · {data.city}
                </div>
                {/* El mapa REAL, no el marcador de posición del maquetado: se carga en
                    diferido y degrada solo si faltan los tiles. El pequeño no se puede mover:
                    sitúa el inmueble. Para explorar, se pincha y se abre grande. */}
                <div className={styles.map}>
                  <PropertyMap markers={mapMarkers} height={168} interactive={false} />
                  <button
                    type="button"
                    className={styles.mapExpand}
                    onClick={() => setMapOpen(true)}
                    aria-label="Ampliar el mapa"
                  >
                    <span className={styles.mapExpandBadge}>
                      <Icon name="expand" size={13} strokeWidth={2.4} />
                      Ampliar
                    </span>
                  </button>
                </div>
              </Card>

              <Card variant="sm">
                <div className={styles.valuationLabel} style={{ marginBottom: 14 }}>
                  Gastos mensuales
                </div>
                <div className={styles.expenseList}>
                  {data.expenseBreakdown.lines.map((line) => (
                    <div key={`${line.origin}-${line.concept}`} className={styles.expenseRow}>
                      <span className={styles.expenseConcept}>
                        {line.concept}
                        {/* Si el gasto no se factura mensualmente, se enseña el importe real
                            para que el desglose sea auditable y no parezca que el IBI se paga
                            cada mes. */}
                        {line.frequency !== 'mensual' ? (
                          <span className={styles.expenseRaw}>
                            {formatEur(line.rawAmount)}/
                            {line.frequency === 'anual' ? 'año' : 'trim.'}
                          </span>
                        ) : null}
                      </span>
                      <span className={styles.expenseAmount}>{formatEur(line.amount)}</span>
                    </div>
                  ))}
                  <hr className={styles.expenseDivider} />
                  <div className={styles.expenseTotalRow}>
                    <span className={styles.expenseTotalLabel}>Total</span>
                    <span className={styles.expenseTotalAmount}>
                      {formatEur(data.expenseBreakdown.total)}
                    </span>
                  </div>
                </div>
              </Card>

              <Card variant="sm">
                <div className={styles.valuationLabel} style={{ marginBottom: 14 }}>
                  Comentarios
                </div>
                <div className={styles.notes}>
                  {data.notes ?? 'Sin comentarios registrados para este inmueble.'}
                </div>
              </Card>

              {/* El plano subido en Documentos; si no hay ninguno, no ocupa sitio. */}
              <PropertyFloorPlan propertyId={data.id} canManage={canDocuments} />
            </div>
          </div>

          {mapOpen && (
            <MapModal
              title={data.name}
              subtitle={`${data.addressLine}${data.postalCode ? `, ${data.postalCode}` : ''} · ${data.city}`}
              markers={mapMarkers}
              onClose={() => setMapOpen(false)}
            />
          )}
        </>
      ),
    },
    {
      id: 'habitaciones',
      label: 'Habitaciones',
      count: data.units.length,
      render: () => (
        <>
          {/* --- Habitaciones: el detalle de la ocupación real --------------------- */}
          <PropertyUnits
            propertyId={data.id}
            units={data.units}
            occupancyRate={data.financials.occupancyRate}
            monthlyRent={data.financials.monthlyRent}
            canManage={canUnits}
          />
        </>
      ),
    },
    {
      id: 'documentos',
      label: 'Documentos',
      count: data.documents.length,
      render: () => (
        <PropertyDocuments propertyId={data.id} canManage={canDocuments} />
      ),
    },
    // Última, y solo si el inmueble la tiene activada (o ya tiene algo registrado: ocultar un
    // préstamo que existe sería peor que enseñar la pestaña).
    ...(mostrarFinanciacion
      ? [
          {
            id: 'financiacion',
            label: 'Financiación',
            count: financiacionRegistrada,
            render: () => (
              <PropertyFinancing
                propertyId={data.id}
                financing={data.financing}
                units={data.units}
                canManage={canFinance}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <section>
      <PageHeader
        size="detail"
        title={data.name}
        titleAside={<PropertyInfoButton property={data} />}
        subtitle={data.subtitle}
        backTo="/propiedades"
        backLabel="Volver a Propiedades"
        actions={<PropertyActions property={data} />}
      />

      <Tabs
        tabs={tabs}
        ariaLabel="Secciones del inmueble"
        aside={<OccupancyStrip propertyId={data.id} units={data.units} occupancyRate={data.financials.occupancyRate} />}
      />
    </section>
  );
}

/**
 * Ocupación del inmueble, en la barra de pestañas para que se vea desde CUALQUIER pestaña.
 *
 * Sale de las HABITACIONES (unidades), no de los dormitorios, igual que en el listado y en el
 * panel. Por eso enseña también el «2 de 3»: así se ve de dónde sale el porcentaje.
 */
function OccupancyStrip({
  propertyId,
  units,
  occupancyRate,
}: {
  propertyId: string;
  units: PropertyDetailResponse['units'];
  occupancyRate: number;
}) {
  const occupied = units.filter((unit) => unit.status === 'ocupada').length;
  /*
   * Y al lado, la ROTACIÓN del año: salidas ÷ habitaciones. La ocupación dice cómo está hoy; la
   * rotación, cuántas veces se ha vaciado en el año, que es lo que cuesta limpiezas y meses vacíos.
   */
  const rotacion = useQuery({ queryKey: queryKeys.rotation(), queryFn: () => api.rotation() });
  const rot = rotacion.data?.data.find((fila) => fila.propertyId === propertyId) ?? null;
  if (units.length === 0) {
    return (
      <div className={styles.occupancy}>
        <span className={styles.occupancyLabel}>Ocupación</span>
        <span className={styles.occupancyCount}>Sin habitaciones</span>
      </div>
    );
  }
  return (
    <div className={styles.occupancy}>
      <span className={styles.occupancyLabel}>Ocupación</span>
      <span className={styles.occupancyValue}>{formatPercent(occupancyRate, 0)}</span>
      <ProgressBar className={styles.occupancyBar} percent={occupancyRate} label="Ocupación del inmueble" />
      <span className={styles.occupancyCount}>
        {occupied} de {units.length}
      </span>
      {rot && rot.rate !== null && rotacion.data && (
        <span
          className={styles.rotation}
          title={`${rot.moveOuts} ${rot.moveOuts === 1 ? 'salida' : 'salidas'} y ${rot.moveIns} ${
            rot.moveIns === 1 ? 'entrada' : 'entradas'
          } en ${rotacion.data.year}, para ${rot.units} ${rot.units === 1 ? 'habitación' : 'habitaciones'}${
            rot.averageStayDays ? `. Estancia media de los que se fueron: ${Math.max(1, Math.round(rot.averageStayDays / 30.4))} meses` : ''
          }. Las renovaciones no cuentan.`}
        >
          Rotación {rotacion.data.year} <strong>{Math.round(rot.rate * 100)} %</strong>
        </span>
      )}
    </div>
  );
}

/**
 * Pestaña de Documentos.
 *
 * ANTES ERAN DOS TARJETAS y una repetía a la otra: a la izquierda la lista por tipos y a la
 * derecha un «Archivo» con los mismos ficheros ordenados por fecha. Cada documento salía dos
 * veces en la misma pantalla, con dos botones de descarga distintos.
 *
 * La lista por tipos gana porque contesta la pregunta que se hace de verdad —«¿me falta
 * algo?»— y no solo «¿qué hay?». Lo que subieras fuera de esos tipos sigue estando en la
 * pantalla general de Documentos, que es el archivo de toda la organización.
 */
function PropertyDocuments({
  propertyId,
  canManage,
}: {
  propertyId: string;
  canManage: boolean;
}) {
  return <PropertyDeeds propertyId={propertyId} canManage={canManage} />;
}